import { displayBrokerageName } from "./broker-license-requirements";

export type BrokerSignature = {
  name: string;
  title?: string | null;
  company?: string | null;
  phone?: string | null;
  website?: string | null;
  linkedInUrl?: string | null;
  licenseLine?: string | null;
  logoUrl?: string | null;
  /** Theme logo width. Email signatures clamp this so a wordmark stays readable. */
  logoWidth?: number | null;
  accentColor?: string | null;
};

export function defaultBrokerSignatureTitle(
  kind: string | null | undefined,
): string {
  return kind === "ACQUIRER" ? "Acquisition expert" : "Business broker";
}

/** Organization / brokerage line. Never repeat the person's name. */
export function resolveSignatureCompany(input: {
  name: string;
  brokerageName?: string | null;
  organizationName?: string | null;
  brandCompanyName?: string | null;
}): string | undefined {
  const company =
    displayBrokerageName({
      brokerageName: input.brokerageName,
      organization: { name: input.organizationName },
    }) ??
    input.brandCompanyName?.trim() ??
    null;
  if (!company) return undefined;
  if (
    company.localeCompare(input.name.trim(), undefined, {
      sensitivity: "accent",
    }) === 0
  ) {
    return undefined;
  }
  return company;
}

export function buildAutoSignature(input: {
  name: string;
  title?: string | null;
  company?: string | null;
  phone?: string | null;
  website?: string | null;
  linkedInUrl?: string | null;
  licenseNumber?: string | null;
  logoUrl?: string | null;
  logoWidth?: number | null;
  accentColor?: string | null;
}): BrokerSignature {
  const license = input.licenseNumber?.trim();
  return {
    name: input.name,
    title: input.title,
    company: input.company,
    phone: input.phone,
    website: input.website,
    linkedInUrl: input.linkedInUrl,
    licenseLine: license ? `License ${license}` : undefined,
    logoUrl: input.logoUrl,
    logoWidth: input.logoWidth,
    accentColor: input.accentColor,
  };
}

export function renderSignatureText(signature: BrokerSignature): string {
  const lines = [signature.name.trim()];
  const role = roleLine(signature);
  if (role) lines.push(role);
  for (const item of contactItems(signature)) lines.push(item.text);
  const license = signature.licenseLine?.trim();
  if (license) lines.push(license);
  return lines.join("\n");
}

export function renderSignatureHtml(signature: BrokerSignature): string {
  const accent = safeAccent(signature.accentColor);
  const body = signatureBody(signature);
  const logo = logoImage(signature);
  const font = "font-family:Arial,Helvetica,sans-serif";

  const rows = logo
    ? `<tr><td style="padding:0 0 12px 0">${logo}</td></tr><tr><td style="border-top:2px solid ${accent};padding-top:12px;${font}">${body}</td></tr>`
    : `<tr><td style="border-left:3px solid ${accent};padding:0 0 0 14px;${font}">${body}</td></tr>`;

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse"><tbody>${rows}</tbody></table>`;
}

function signatureBody(signature: BrokerSignature): string {
  const name = `<div style="font-size:16px;line-height:20px;font-weight:700;color:#111111">${escapeHtml(signature.name.trim())}</div>`;
  const role = roleHtml(signature);
  const contacts = contactHtml(contactItems(signature));
  const license = signature.licenseLine?.trim()
    ? `<div style="padding-top:8px;font-size:12px;line-height:16px;color:#666666">${escapeHtml(signature.licenseLine.trim())}</div>`
    : "";
  return `${name}${role}${contacts}${license}`;
}

function roleHtml(signature: BrokerSignature): string {
  const parts = [signature.title, signature.company]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .map(escapeHtml);
  if (parts.length === 0) return "";
  return `<div style="padding-top:2px;font-size:13px;line-height:18px;color:#555555">${parts.join(" &#183; ")}</div>`;
}

function roleLine(signature: BrokerSignature): string {
  return [signature.title, signature.company]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(" · ");
}

type ContactItem = { href: string; label: string; text: string };

function contactItems(signature: BrokerSignature): ContactItem[] {
  const items: ContactItem[] = [];
  const phone = signature.phone?.trim();
  if (phone) {
    const digits = phone.replace(/\D/g, "");
    const label = formatSignaturePhone(phone);
    items.push({
      href: digits.length >= 7 ? `tel:+${digits}` : "",
      label,
      text: label,
    });
  }

  const website = httpUrl(signature.website);
  if (website) {
    const label = website.hostname.replace(/^www\./, "");
    items.push({ href: website.href, label, text: label });
  }

  const linkedIn = httpUrl(signature.linkedInUrl);
  if (linkedIn) {
    const path = linkedIn.pathname.replace(/\/$/, "");
    const host = linkedIn.hostname.replace(/^www\./, "");
    items.push({
      href: linkedIn.href,
      label: "LinkedIn",
      text: `${host}${path}`,
    });
  }

  return items;
}

/** NANP numbers read as (917) 232-1610. Anything else stays as entered. */
function formatSignaturePhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1")
      ? digits.slice(1)
      : digits.length === 10
        ? digits
        : null;
  if (!national) return value.trim();
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

function httpUrl(value: string | null | undefined): URL | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const candidate = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : trimmed.includes(".")
      ? `https://${trimmed}`
      : null;
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    url.hash = "";
    return url;
  } catch {
    return null;
  }
}

function contactHtml(items: ContactItem[]): string {
  if (items.length === 0) return "";
  const body = items
    .map((item) => linkHtml(item))
    .join('<span style="color:#999999"> &#183; </span>');
  return `<div style="padding-top:10px;font-size:13px;line-height:18px;color:#222222">${body}</div>`;
}

function linkHtml(item: ContactItem): string {
  const label = escapeHtml(item.label);
  if (!item.href) return label;
  const href = escapeHtml(item.href);
  return `<a href="${href}" style="color:#222222;text-decoration:none"><span style="color:#222222;text-decoration:none">${label}</span></a>`;
}

function logoImage(signature: BrokerSignature): string {
  const url = httpUrl(signature.logoUrl);
  if (!url) return "";
  const width = signatureLogoWidth(signature.logoWidth);
  return `<img src="${escapeHtml(url.href)}" alt="" width="${width}" style="display:block;width:${width}px;max-width:${width}px;height:auto;border:0;outline:none;text-decoration:none">`;
}

function signatureLogoWidth(width: number | null | undefined): number {
  if (width == null || !Number.isFinite(width)) return 160;
  return Math.min(180, Math.max(40, Math.round(width)));
}

function safeAccent(value: string | null | undefined): string {
  const raw = value?.trim() ?? "";
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw) ? raw : "#222222";
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
